<template>
    <div>
        <div class="modal-small-toolbar">
            <div>
                <span v-if="! editingKeyDate && ! creatingKeyDate">Key Dates</span>
                <div v-if="creatingKeyDate || editingKeyDate" class="space-x-2">
                    <a @click="creatingKeyDate = editingKeyDate = false">Key Dates</a>
                    <span>/</span>
                    <span v-if="creatingKeyDate">Create New Key Date</span>
                    <span v-if="editingKeyDate">Edit Key Date</span>
                </div>
            </div>
        </div>
        <div v-if="!editingKeyDate && !creatingKeyDate" class="w-full p-4 mr-2">
            <table class="w-full" v-if="leases.selectedLease?.key_dates?.length">
                <thead>
                    <tr>
                        <th class="text-center">Occurs On</th>
                        <th>Type</th>
                        <th class="text-center">Clause</th>
                        <th class="text-center">Status</th>
                        <th>Note</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="date in orderBy(leases.selectedLease?.key_dates || [], 'occurs_on')" :key="date.id">
                        <td class="text-center">{{ date.occurs_on | date }}</td>
                        <td>{{ date.type }}</td>
                        <td class="text-center">{{ date.lease_clause }}</td>
                        <td class="text-center font-variant-all-small-caps">
                            <div v-if="! date.completed_at">
                                <span class="badge badge-red">Incomplete</span>
                            </div>
                            <div v-else class="leading-normal">
                                <span class="badge badge-green">Completed</span>
                            </div>
                        </td>
                        <td>
                            <v-popover v-if="date.note" trigger="hover click" offset="10">
                                <inline-svg name="note"></inline-svg>

                                <template slot="popover">
                                    <div class="leading-normal max-w-sm p-6">
                                        {{ date.note }}
                                    </div>
                                </template>
                            </v-popover>
                        </td>
                        <td class="flex justify-center space-x-3">
                            <button v-if="canUpdateKeyDate" @click="editKeyDate(date.id)" type="button">
                                <inline-svg name="edit-pencil" classes="w-5 h-5 cursor-pointer"></inline-svg>
                            </button>
                            <button v-if="canDeleteKeyDate" @click="confirmDeleteKeyDate(date.id)" type="button">
                                <inline-svg name="trash"></inline-svg>
                            </button>
                        </td>
                    </tr>
                </tbody>
            </table>
            <div v-else class="mt-8 flex flex-col h-full items-center justify-center space-y-2">
                <span class="text-xl">No Key Dates Found</span>
                <button
                    v-if="canCreateKeyDate"
                    type="button"
                    @click.prevent="createKeyDate"
                    class="flex items-center btn btn-small btn-indigo">
                        <inline-svg name="add" classes="w-4 h-4 text-white fill-current"></inline-svg>
                        <span class="ml-2">New Key Date</span>
                </button>
            </div>
            <button v-if="leases.selectedLease?.key_dates?.length && canCreateKeyDate" @click="createKeyDate" type="button" class="my-3 flex items-center float-right btn btn-small btn-indigo">
                <inline-svg name="add-white"></inline-svg>
                <span>Create Key Date</span>
            </button>
        </div>

        <div v-else>
            <form>
                <div class="p-4 mr-2">
                    <div class="flex">
                        <div class="flex flex-1 flex-col shadow-md rounded mr-6 p-6 bg-gray-200 border border-gray-300">
                            <div class="flex mb-4">
                                <div class="mr-4">
                                    <label for="occurs_on">Occurs On</label>
                                    <date-picker
                                        v-model="keyDate.occurs_on"
                                        lang="en"
                                        :first-day-of-week="7"
                                        format="MM/DD/YYYY"
                                        :value-type="valueType"
                                        input-name="occurs_on"
                                        input-class="form-input"
                                        :input-attr="{ size: 11, required: true }">
                                    </date-picker>
                                    <div v-if="errors.occurs_on" class="form-error">{{ errors.occurs_on[0] }}</div>
                                </div>
                                <div class="mr-4">
                                    <label for="type">Type</label>
                                    <select v-model="keyDate.type" class="mt-1 form-select">
                                        <option value=""></option>
                                        <option value="Expiration">Expiration</option>
                                        <option value="Renewal">Renewal</option>
                                        <option value="Cancellation">Cancellation</option>
                                        <option value="Expansion">Expansion</option>
                                        <option value="Notice of Renewal">Notice of Renewal</option>
                                        <option value="Option">Option</option>
                                        <option value="Status Change">Status Change</option>
                                    </select>
                                    <div v-if="errors.type" class="form-error">{{ errors.type[0] }}</div>
                                </div>
                                <div class="mr-4">
                                    <label for="lease_clause">Clause</label>
                                    <input v-model="keyDate.lease_clause" type="text" class="mt-1 form-input" name="lease_clause">
                                    <div v-if="errors.lease_clause" class="form-error">{{ errors.lease_clause[0] }}</div>
                                </div>
                            </div>

                            <div>
                                <textarea v-model="keyDate.note" class="w-full form-textarea text-sm" cols="10" rows="5" name="note" placeholder="Key Date Notes"></textarea>
                            </div>
                        </div>

                        <div>
                            <div class="flex justify-between items-center border-b border-dotted border-gray-300 mb-4 pb-3">
                                <label class="mr-10">Completed?</label>
                                <toggle-switch v-model="isCompleted"></toggle-switch>
                            </div>
                            <div v-if="isCompleted">
                                <div class="flex justify-between items-center border-b border-dotted border-gray-300 mb-4 pb-3">
                                    <label class="mr-10">On</label>
                                    <date-picker
                                        v-model="keyDate.completed_at"
                                        lang="en"
                                        :first-day-of-week="7"
                                        format="MM/DD/YYYY"
                                        :value-type="valueType"
                                        input-name="completed_at"
                                        input-class="form-input"
                                        :input-attr="{ size: 11, required: true }">
                                    </date-picker>
                                </div>
                                <div class="flex justify-between items-center border-b border-dotted border-gray-300 mb-4 pb-3">
                                    <label class="mr-10">By</label>
                                    <select v-model="keyDate.completed_by" class="form-select">
                                        <option value=""></option>
                                        <option v-for="user in orderBy(users, 'name')" :key="user.id" :value="user.id">{{ user.name }}</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
    import moment from 'moment';
    import { mapState } from 'vuex';
    import DatePicker from 'vue2-datepicker';

    export default {
        components: {
            DatePicker
        },

        props: {
            formErrors: {
                type: [String, Object],
                required: false
            }
        },

        data() {
            return {
                editingKeyDate: false,
                creatingKeyDate: false,
                selectedKeyDateId: '',
                users: '',
                isCompleted: '',

                valueType: {
                    value2date: (value) => {
                        return value ? moment(value, 'YYYY-MM-DD').toDate() : null
                    },
                    date2value: (date) => {
                        return date ? moment(date).format('YYYY-MM-DD') : null
                    }
                }
            }
        },

        created() {
            this.$parent.$parent.$on('keyDateSaved', (keyDateId) => {
                this.selectedKeyDateId = keyDateId;

                this.creatingKeyDate = false;

                this.editingKeyDate = true;
            });
        },

        mounted() {
            axios.get('/api/clients/' + window.user.client_id + '/users').then((response) => {
                this.users = response.data;
            }).catch((error) => {
                this.$notify({
                    type: 'error',
                    title: 'Error',
                    text: 'Error pulling User data, please try again later'
                });
            });
        },

        watch: {
            selectedKeyDateId() {
                this.isCompleted = this.keyDate.completed_at != null;
                this.$emit('selectedKeyDateChange', this.selectedKeyDateId);
            },

            creatingKeyDate() {
                if (! this.creatingKeyDate && this.selectedKeyDateId == 'new') {
                    this.removeKeyDate('new');
                }
            },

            keyDates() {
                if (this.leases.selectedLease) {
                    if (this.creatingKeyDate && ! _.find(this.leases.selectedLease.key_dates, ['id', 'new'])) {
                        this.selectedKeyDateId = _.last(this.leases.selectedLease.key_dates).id;
                        this.creatingKeyDate = false;
                    }
                }
            },

            isCompleted() {
                if (! this.isCompleted) {
                    this.keyDate.completed_at = '';
                    this.keyDate.completed_by = '';
                }
            }
        },

        computed: {
            ...mapState(['sites']),
            ...mapState(['leases']),
            
            canCreateKeyDate() {
                return this.$gate.can('create', 'site');
            },
            
            canUpdateKeyDate() {
                return this.$gate.can('update', 'site', this.leases.selectedLease?.site_id);
            },
            
            canDeleteKeyDate() {
                return this.$gate.can('delete', 'site', this.leases.selectedLease?.site_id);
            },

            keyDate() {
                return this.selectedKeyDateId == ''
                    ? ''
                    : _.find(this.leases.selectedLease.key_dates, ['id', this.selectedKeyDateId]);
            },

            keyDates() {
                return this.leases.selectedLease ? this.leases.selectedLease.key_dates : {};
            },

            errors() {
                return this.formErrors;
            }
        },

        methods: {
            createKeyDate() {
                this.creatingKeyDate = true;

                this.leases.selectedLease.key_dates.push({
                    id: 'new',
                    lease_id: this.leases.selectedLease.id,
                    occurs_on: '',
                    type: '',
                    lease_clause: '',
                    note: ''
                });

                this.selectedKeyDateId = 'new';
            },

            editKeyDate(keyDateId) {
                this.selectedKeyDateId = keyDateId;

                this.creatingKeyDate = false;
                this.editingKeyDate = true;
            },

            removeKeyDate(keyDateId) {
                _.remove(this.leases.selectedLease.key_dates, (date) => {
                    return date.id == keyDateId
                });

                this.$forceUpdate();
            },

            confirmDeleteKeyDate(keyDateId) {
                if (keyDateId == "new") {
                    this.removeKeyDate('new');

                    return;
                }

                this.$swal({
                    title: 'Are you sure?',
                    text: "Are you sure you want to delete this Key Date?",
                    type: 'warning',
                    buttonsStyling: false,
                    confirmButtonText: 'Yes - Delete it',
                    confirmButtonClass: 'btn btn-red',
                    showCancelButton: true,
                    cancelButtonClass: 'btn btn-secondary mr-4',
                    reverseButtons: true
                }).then((result) => {
                    if (result.value) {
                        this.deleteKeyDate(keyDateId);
                    }
                });
            },

            deleteKeyDate(keyDateId) {
                axios.delete('/leases/' + this.leases.selectedLease.id + '/key-dates/' + keyDateId)
                    .then((response) => {
                        this.$notify({
                            type: 'success',
                            title: 'Success',
                            text: response.data.message
                        });

                        this.removeKeyDate(keyDateId);

                        this.$eventHub.$emit('reloadSite');
                    })
                    .catch((error) => {
                        this.$notify({
                            type: 'error',
                            title: 'Error',
                            text: error.response.data.message
                        });
                    }
                );
            },
        },
    }
</script>
